import type { WebLocale } from '@/enums/web-locale';
import type { TrackSraEvent } from '@/helpers/composables/useBackendTracking';

import { ExternalTracker, SraEventAction, SraPlacement } from '@/@types/graphql-types';

import { Platform } from '@/@types/graphql-types';
import { COUNTRY_EXCEPTIONS, GDPR_WEB_LOCALES } from '@/constants/web-locales.constant';

import { captureMessageForSentry } from '@/libraries/sentry';
import { getSrFromBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';

// Timestamp for the macro
const timestamp = Date.now().toString();

const MACRO_TIMESTAMP = /\[timestamp\]/i;
const MACRO_CACHEBUSTER = /\[cachebuster\]/i;
const MACRO_CLICKOUT_WEBLOCALE = /\[kw_weblocale\]/i;
const MACRO_GDPR_CONSENT = /\${GDPR}/i; // Matches both '${GDPR}' and '${gdpr}'
const MACRO_GDPR_CONSENT_755 = /\$\{GDPR_CONSENT_[^}]*\}/i; // Matches both '${GDPR_CONSENT_XXXXX}' and '${gdpr_consent_XXXXX}'
const MACRO_GDPR_PD = /\${GDPR_PD}/i; // Matches both '${GDPR_PD}' and '${gdpr_pd}'
const MACRO_GPP_STRING_755 = /gpp=\${(GPP_STRING_)([^]*?)};/i; // Matches both 'gpp=${GPP_STRING_XXX};' and 'gpp=${gpp_string_XXX};'
const MACRO_GPP_SID = /gpp_sid=\${GPP_SID};?/i; // Matches both 'gpp_sid=${GPP_SID};' and 'gpp_sid=${gpp_sid};'

// Eulerian macros
const MACRO_EA_RANDOM = /ea-rnd=%n/;

// Exactag Macros
const MACRO_EXACTAG_TIMESTAMP = /rnd=TIMESTAMP/;
const MACRO_EXACTAG_SUPPLIER = /extPu=/;
const MACRO_EXACTAG_PLATFORM = /extSi=/;
const MACRO_EXACTAG_PLACEMENT = /extPm=/;
const MACRO_EXACTAG_CAMPAIGN_NAME = /extLi=/;

type CommonTrackerType = {
	webLocale?: WebLocale;
};

type FormatMacroImpressionType = CommonTrackerType & {
	tracker: string;
	placement?: SraPlacement | null;
	campaignName?: string;
};
export type ExternalTrackerFiredType =
	| SraEventAction.ExternalClickoutTrackerFired
	| SraEventAction.ExternalImpressionTrackerFired;

type HandleExternalTrackerType = CommonTrackerType & {
	externalTrackers: ExternalTracker[];
	trackerType: ExternalTrackerFiredType;
	bidId: string;
	skipFetchExternalTrackers?: boolean;
	trackSraEvent?: TrackSraEvent;
	buyBoxType?: NewBuyBoxView;
};

type TrackerType = {
	externalTrackers?: ExternalTracker[];
	bidId: string;
	webLocale: WebLocale;
	trackSraEvent?: TrackSraEvent;
	buyBoxType?: NewBuyBoxView;
};

/**
 * Formatting an impression tracker, that may include impressions or clickout URL
 * This is mainly used for SRs
 *
 * At the moment, the trackers are only fired if consent is given,
 * so we can safely assume that this replacement is set to '1'.
 *
 * @param impression a URL with the included [timestamp] macro
 * @param webLocale The web locale of the page the user is viewing the app from
 * @param placement The placement of the SR campaign
 * @param campaignName The name of the SR campaign
 *
 * @returns a formatted tracker
 */
export function formatExternalTracker({
	tracker,
	webLocale,
	placement,
	campaignName,
}: FormatMacroImpressionType): string {
	if (!tracker.length) return '';

	const isLocaleGDPR = webLocale && GDPR_WEB_LOCALES.includes(webLocale);
	const cachebuster = `${timestamp}${Math.random().toString(36).substring(2)}`;

	tracker = tracker.replace(MACRO_TIMESTAMP, timestamp);
	tracker = tracker.replace(MACRO_CACHEBUSTER, cachebuster);
	if (webLocale) tracker = tracker.replace(MACRO_CLICKOUT_WEBLOCALE, webLocale);
	tracker = tracker.replace(MACRO_GDPR_CONSENT, isLocaleGDPR ? '1' : '0');
	tracker = tracker.replace(MACRO_EA_RANDOM, `ea-rnd=${timestamp}`);
	tracker = tracker.replace(MACRO_GDPR_PD, '0');
	tracker = tracker.replace(MACRO_GDPR_CONSENT_755, getTCString('ExternalTracker') ?? '');

	// Remove gpp macros entirely for now (values depends on experiment's outcome)
	tracker = tracker.replace(MACRO_GPP_STRING_755, '');
	tracker = tracker.replace(MACRO_GPP_SID, '');

	// Exactag Macros
	tracker = tracker.replace(MACRO_EXACTAG_TIMESTAMP, `rnd=${timestamp}`);
	tracker = tracker.replace(MACRO_EXACTAG_PLATFORM, `extSi=${Platform.Web.toLowerCase()}`);
	tracker = tracker.replace(MACRO_EXACTAG_SUPPLIER, `extPu=justwatch`);

	// Only fill in macros if received placement and campaignName
	if (placement) tracker = tracker.replace(MACRO_EXACTAG_PLACEMENT, `extPm=${placement}`);
	if (campaignName) tracker = tracker.replace(MACRO_EXACTAG_CAMPAIGN_NAME, `extLi=${campaignName}`);

	return tracker;
}

/**
 * A TC String’s primary purpose is to encapsulate and encode all the information disclosed
 * to a user and the expression of their preferences for their personal data processing
 * under the GDPR.
 *
 * The TC String is generated by the Consent Management Platform (e.g. Usercentrics CMP v3),
 * and is accessed via the `__tcfapi` function. We store it in userStore & localStorage when consent is given or updated.
 *
 * @param placement Optional parameter to specify the placement of the tracker for sentry logging.
 *
 * @returns The Transparency and Consent string based on the user consent
 */
export function getTCString(placement?: 'ClickoutUrl' | 'ExternalTracker') {
	const userStore = useUserStore();
	const languageStore = useLanguageStore();

	const tcString = import.meta.client ? sessionStorage.getItem('tcString') ?? userStore.tcString : userStore.tcString;

	// Add sentry error to trigger alerts when TC String is not available but required
	if (!tcString && GDPR_WEB_LOCALES.includes(languageStore.webLocale)) {
		const strictlyAllConsentsGranted = userStore.strictlyAllConsentsGranted;
		const consentsMessage = strictlyAllConsentsGranted ? 'strictlyAllConsentsGranted' : 'noConsentsGranted';

		captureMessageForSentry(
			'[GDPRConsent - getTCString] required TC String is null.',
			{ where: '[impression-tracker-helper.ts] > getTCString()' },
			'error',
			{ consent: consentsMessage, 'consent.placement': placement }
		);
	}

	return tcString;
}

export async function storeTCString() {
	if (!import.meta.client || typeof window.__tcfapi !== 'function') {
		return null;
	}

	try {
		const tcData = await new Promise<{ tcString: string } | null>(resolve => {
			window.__tcfapi?.('getTCData', 2, (data, success) => {
				if (success && data?.tcString) {
					resolve(data);
				} else {
					resolve(null);
				}
			});
		});

		if (tcData?.tcString == null) return null;

		if (import.meta.client) sessionStorage.setItem('tcString', tcData.tcString);

		return tcData.tcString;
	} catch (error: any) {
		// Webapp can init, but better keep tracking as this is still a limited app experience (no settings)

		captureMessageForSentry(
			'[User Centrics] __tcfapi() error',
			{ error, where: '[impression-tracker-helper.ts] storeTCString()' },
			'error'
		);

		return null;
	}
}

/**
 * Loop through all our external tracker urls, allowing us to
 * have multiple trackers
 *
 * @param externalTrackers An array of external trackers
 * @param trackerType What type of tracker we are firing
 * @param bidId The ID of the bid, used for tracking
 * @param skipFetchExternalTrackers Used in experiments where we don't want to fire the tracker
 * @param webLocale The web locale of the page the user is viewing the app from
 * @param trackSraEvent Optional tracking function to send SRA events
 */
export const handleExternalClickoutTrackers = ({
	externalTrackers,
	trackerType,
	bidId,
	skipFetchExternalTrackers,
	webLocale,
	trackSraEvent,
	buyBoxType,
}: HandleExternalTrackerType) => {
	const { placement, sponsoredAd } = getSrFromBidId(bidId);
	externalTrackers.forEach(async ({ data }) => {
		if (data) {
			// TODO: if env variable is not production, handle the URLs differently
			const url = formatExternalTracker({
				tracker: data,
				webLocale,
				placement,
				campaignName: sponsoredAd?.campaign?.name,
			});

			/**
			 * At the moment we cannot get the status due to no cors
			 * So we will manually say confirmed for now
			 */
			let status = 200;

			if (!skipFetchExternalTrackers) {
				try {
					await fetch(url, {
						mode: 'no-cors',
						cache: 'no-cache',
					});
				} catch (err: any) {
					captureMessageForSentry(
						'[Unable to send the external tracker]:',
						{
							error: err,
							where: '[impression-tracker-helper.js]: handleExternalClickoutTrackers()',
							requestInfo: {
								tracker: data,
								trackerType: trackerType,
								webLocale,
								placement,
								campaignName: sponsoredAd?.campaign?.name,
							},
						},
						'error'
					);

					status = 418;
				}
			}

			if (trackSraEvent) {
				trackSraEvent({
					action: trackerType,
					sponsoredAd: sponsoredAd ?? undefined,
					externalTrackerURL: data,
					externalTrackerStatus: status,
					externalTrackerURLExpanded: url,
					buyBoxType,
				});
			}
		}
	});
};

/**
 * Inject external HTML trackers into the body of the page
 *
 * @param externalTrackers An array of external trackers
 * @param webLocale The web locale of the page the user is viewing the app from
 * @param bidId The ID of the bid, used for tracking
 */
export const injectExternalHtmlTrackers = ({
	externalTrackers,
	webLocale,
	bidId,
}: {
	externalTrackers: ExternalTracker[];
	webLocale: WebLocale;
	bidId: string;
}) => {
	const { placement, sponsoredAd } = getSrFromBidId(bidId);

	externalTrackers.forEach(({ data }) => {
		if (data) {
			const formatImpression = formatExternalTracker({
				tracker: data,
				webLocale,
				placement,
				campaignName: sponsoredAd?.campaign?.name,
			});
			const fragment = document.createRange().createContextualFragment(formatImpression);

			document.head.appendChild(fragment);
		}
	});
};

/**
 * Check to see if we have consent before possibility handling external clickout trackers
 *
 * @param externalTrackers An array of external trackers
 * @param webLocale The web locale of the page the user is viewing the app from
 * @param bidId The ID of the bid, used for tracking
 * @param trackSraEvent Optional tracking function to send SRA events
 */
export async function externalTrackingClickoutUrl({
	externalTrackers,
	bidId,
	webLocale,
	trackSraEvent,
	buyBoxType,
}: TrackerType) {
	if (!externalTrackers?.length) return;

	handleExternalClickoutTrackers({
		externalTrackers: externalTrackers ?? [],
		trackerType: SraEventAction.ExternalClickoutTrackerFired,
		webLocale,
		bidId,
		trackSraEvent,
		buyBoxType,
	});
}

/**
 * Check to see if we have consent before possibility handling external impression trackers
 *
 * @param externalTrackers An array of external trackers
 * @param webLocale The web locale of the page the user is viewing the app from
 * @param bidId The ID of the bid, used for tracking
 * @param trackSraEvent Optional tracking function to send SRA events
 */
export function externalTrackingImpressionUrl({
	externalTrackers,
	bidId,
	webLocale,
	trackSraEvent,
	buyBoxType,
}: TrackerType) {
	if (!externalTrackers?.length) return;

	handleExternalClickoutTrackers({
		externalTrackers: externalTrackers,
		trackerType: SraEventAction.ExternalImpressionTrackerFired,
		webLocale,
		bidId,
		trackSraEvent,
		buyBoxType,
	});
}

/**
 * Check to see if we have consent before possibility handling inject HTML trackers
 *
 * @param externalTrackers An array of external trackers
 * @param webLocale The web locale of the page the user is viewing the app from
 * @param bidId The ID of the bid, used for tracking
 */
export function injectExternalImpressionHtml({ externalTrackers, bidId, webLocale }: TrackerType) {
	if (!externalTrackers?.length) return;

	injectExternalHtmlTrackers({
		externalTrackers: externalTrackers,
		webLocale,
		bidId,
	});
}

/** -- Impression Pixel URLs -- */

const HBO_IMPRESSION_PIXEL_URL =
	'https://d.agkn.com/pixel/10690/?che=[cachebuster]&cmid=33320798&sid=8725507&pid=447199725&cgid=640372217&cid=256085031&aid=15090211';

export function getHboImpressionUrl(country: string): string | null {
	if (country !== 'US') return null;
	return formatExternalTracker({ tracker: HBO_IMPRESSION_PIXEL_URL });
}

const CRUNCHYROLL_IMPRESSION_PIXEL_URL = 'https://imp.pxf.io/i/1206980/1704122/16324';

export function getCrunchyrollImpressionUrl(country: string) {
	if (country !== 'US') return null;
	return CRUNCHYROLL_IMPRESSION_PIXEL_URL;
}

/** -- Clickout Trackers -- */
/** -- Crunchyroll DoubleClick Clickout Tracker --
 * Fire the CrunchyRoll DoubleClick clickout tracker.
 * We have been losing ~65% of clickout revenue on CrunchyRoll from alleged issues with Impact
 * This is a potential solution they have suggested. Starting with EU5
 */

const CRU_CLICKOUT_TRACKER =
	'https://ad.doubleclick.net/ddm/trackclk/N299201.279382DBMTP-839561-TRA85/B35669835.444818793;dc_trk_aid=637922952;dc_trk_cid=253313887;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ltd=;dc_tdv=1;kw=[kw_weblocale]';

const ADFORM_CLICKOUT_TRACKER = 'https://track.adform.net/C/?bn=88311368;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_50}';

async function fireClickoutTracker(tracker: string, webLocale: WebLocale, trackerName: string) {
	try {
		await fetch(formatExternalTracker({ tracker, webLocale }), { mode: 'no-cors', cache: 'no-cache' });
	} catch (err: any) {
		captureMessageForSentry(
			`[Unable to send the ${trackerName} clickout tracker]:`,
			{ error: err, where: '[impression-tracker-helper.ts]: fireAllExternalTrackers()' },
			'error'
		);
	}
}

export function fireAllExternalTrackers({
	country,
	shortName,
	bundleId,
}: {
	country?: string | null;
	shortName?: string | null;
	bundleId?: number | null;
}) {
	const isCrunchyroll = shortName === 'cru';
	const isAdform = bundleId === 1066;

	if (!isCrunchyroll && !isAdform) return;

	if (!country) {
		captureMessageForSentry('Missing uct_country - fireAllExternalTrackers', {
			where: '[impression-tracker-helper.ts]: fireAllExternalTrackers()',
		});
		return;
	}

	const countryLower = country.toLowerCase();
	const webLocale = (COUNTRY_EXCEPTIONS[countryLower] ?? countryLower) as WebLocale;

	if (isCrunchyroll) fireClickoutTracker(CRU_CLICKOUT_TRACKER, webLocale, 'CRU');
	if (isAdform) fireClickoutTracker(ADFORM_CLICKOUT_TRACKER, webLocale, 'Adform');
}
